import { Link } from "react-router";
import Ad from "@components/Advertisement/Ad";
import Breadcrumb from "@components/Breadcrumb/Breadcrumb";
import { ActiveType } from "@components/Breadcrumb/types";
import Container from "@components/Container/Container";
import SeoFooter from "@components/Footer/SeoFooter/SeoFooter";
import { GADataProps } from "@components/GAData/GAData";
import Column from "@components/Grid/Column";
import Row from "@components/Grid/Row";
import Header from "@components/Header/Header";
import { useCategoriesFromPath } from "@components/Header/helpers";
import PulseEntryButton from "@features/pulse/components/PulseEntry/PulseEntry";
import { useInjectClientData } from "@hooks/useInjectClientData";
import { useWindowSize } from "@hooks/useWindowSize";
import SubMobileNavigation from "@particles/Navigation/components/SubMobileNavigation";
import BreakingNewsAllCaughtUp from "@routes/BreakingNews/components/BreakingNewsAllCaughtUp";
import BreakingNewsStories from "@routes/BreakingNews/components/BreakingNewsStories";
import { useGetBreakingNewsArticle } from "@routes/BreakingNews/hooks/useGetBreakingNewsArticle";
import { BreakingNewsPageData } from "@routes/BreakingNews/utils/types";
import { NavigationItem } from "@typings/Navigation";
import { sectionNavigationItems } from "@util/constant";
import { cn } from "@util/helpers";
import cx from "classnames";

const BreakingNewsPageContent = ({
  articles: { today, yesterday, thedaybefore },
  sections,
  seoFooterData,
  gaProps,
}: BreakingNewsPageData & { gaProps: GADataProps }): React.ReactElement => {
  useInjectClientData({ gaProps });

  const { isScreenLG } = useWindowSize();
  const { parentCategory } = useCategoriesFromPath();
  const { articles } = useGetBreakingNewsArticle({
    today,
    yesterday,
    thedaybefore,
  });

  const parentCategoryData = sectionNavigationItems?.[parentCategory];
  const items: Record<string, NavigationItem> = {};
  sections.forEach((filter) => {
    const data = sectionNavigationItems[filter];

    if (!data) return;

    const params = new URLSearchParams({ filter });
    const item = { ...data, link: `/breaking-news?${params.toString()}` };

    items[filter] = item;
  });

  const filtersDataMapping = {
    ...sectionNavigationItems,
    "breaking-news": {
      ...sectionNavigationItems["breaking-news"],
      children: sections,
    },
    ...items,
  };

  const todaysArticles = articles.today;
  const yesterdaysArticles = articles.yesterday;
  const thedaybeforesArticles = articles.thedaybefore;

  const totalArticles =
    todaysArticles.length +
    yesterdaysArticles.length +
    thedaybeforesArticles.length;

  return (
    <div className="w-full" data-testid="breaking-news-page-content-component">
      <Ad
        adType="lb1"
        className="flex min-h-[calc(50px+0.50rem)] flex-col items-center justify-center border-b border-gray-175 bg-gray-125 py-1 text-center md:py-3 lg:min-h-[calc(250px+1.5rem)]"
      />

      <PulseEntryButton />

      <Header />

      <Container>
        <Row rootClassName="pt-6">
          <Column rootClassName="mb-6 mx-auto lg:hidden ">
            <SubMobileNavigation />
          </Column>

          <Column rootClassName="w-full mb-6">
            <div className="flex-none">
              <h1
                className={cx(
                  "hidden pb-3 pt-6 font-playfair text-8xl font-semibold text-gray-850 lg:block"
                )}
              >
                <span className="text-10xl">B</span>REAKING{" "}
                <span className="text-10xl">N</span>EWS
              </h1>
            </div>

            <div>
              <Breadcrumb
                activeFilter={articles.activeFilter}
                activeType={ActiveType.QUERY}
                className="border-b-0 border-t border-gray-175 pt-5 lg:border-b lg:border-t-0 lg:border-gray-850"
                parentCategory={"breaking-news"}
                childCategory={"true"}
                categoryDataMapping={filtersDataMapping}
                extraBreadcrumbItems={
                  <div
                    className={cn(
                      "relative box-border flex shrink-0 items-center border-r border-gray-175 pr-3 font-poppins text-sm tracking-tightest text-gray-850 hover:opacity-80",
                      { "font-bold": !articles.activeFilter }
                    )}
                  >
                    <Link
                      to={`${parentCategoryData?.link}?ref=listing-menubar`}
                      reloadDocument
                    >
                      All
                    </Link>
                  </div>
                }
              />
            </div>
          </Column>

          <Column
            rootClassName="w-full lg:w-8/12 mb-6"
            id="breakingnewspage-content-section"
          >
            <div className="space-y-20">
              {todaysArticles.length > 0 ? (
                <BreakingNewsStories articles={todaysArticles} group="today" />
              ) : null}

              {yesterdaysArticles.length > 0 ? (
                <BreakingNewsStories
                  articles={yesterdaysArticles}
                  articlesBefore={todaysArticles.length}
                  group="yesterday"
                  rootClassName="border-gray-850 pt-2 lg:border-t first:border-t-0"
                />
              ) : null}

              {thedaybeforesArticles.length > 0 ? (
                <BreakingNewsStories
                  articles={thedaybeforesArticles}
                  articlesBefore={
                    todaysArticles.length + yesterdaysArticles.length
                  }
                  group="thedaybefore"
                  rootClassName="border-gray-850 pt-2 lg:border-t first:border-t-0"
                />
              ) : null}

              {totalArticles > 0 && totalArticles < 10 ? (
                <Ad
                  adType="bn1"
                  className="w-[calc(100%+5rem)] -translate-x-20 overflow-hidden bg-white lg:w-auto lg:translate-x-0"
                />
              ) : null}
            </div>

            <BreakingNewsAllCaughtUp />
          </Column>

          <Column rootClassName="w-full lg:w-4/12 mb-6">
            <div className="h-full">
              {isScreenLG ? (
                <>
                  {totalArticles <= 18 ? (
                    <Ad adType="imu1" className="lg:sticky lg:top-20" />
                  ) : null}

                  {totalArticles > 18 && totalArticles <= 36 ? (
                    <>
                      <div className="mb-6 lg:h-[800px]">
                        <Ad adType="imu1" className="lg:sticky lg:top-20" />
                      </div>

                      <div className="mb-6 lg:h-[800px]">
                        <Ad adType="imu2" className="lg:sticky lg:top-20" />
                      </div>
                    </>
                  ) : null}

                  {totalArticles > 36 ? (
                    <>
                      <div className="mb-6 lg:h-[800px]">
                        <Ad adType="imu1" className="lg:sticky lg:top-20" />
                      </div>

                      <div className="mb-6 lg:h-[800px]">
                        <Ad adType="imu2" className="lg:sticky lg:top-20" />
                      </div>

                      <Ad adType="imu3" className="lg:sticky lg:top-20" />
                    </>
                  ) : null}
                </>
              ) : null}
            </div>
          </Column>
        </Row>
      </Container>

      <SeoFooter data={seoFooterData} />

      <Ad adType="catfish" />
      <Ad adType="prestitial" />
    </div>
  );
};

export default BreakingNewsPageContent;
